import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { familyService } from '../api/familyService';
import { Family, FamilyMember } from '../types/family.types';
import { AttendeeType } from '../types/enums';
import { Event } from '../types/event.types';

interface UseFamilyMembersReturn {
  family: Family | null;
  loading: boolean;
  error: string | null;
  allMembers: FamilyMember[];
  eligibleMembers: FamilyMember[];
  ineligibleMembers: FamilyMember[];
  refetch: () => Promise<void>;
}

export const useFamilyMembers = (selectedEvent?: Event | null, familyId?: string): UseFamilyMembersReturn => {
  const {
    data: family,
    isLoading: loading,
    error: queryError,
    refetch: queryRefetch,
  } = useQuery({
    queryKey: ['family', familyId],
    queryFn: () => familyService.getMyFamilyDetails(familyId!),
    enabled: !!familyId,
  });

  const refetch = async () => {
    await queryRefetch();
  };

  const error = queryError ? 'Failed to load family data' : null;

  const isMemberEligible = (member: FamilyMember): boolean => {
    if (!selectedEvent?.ageRequirements) return true; // No restrictions

    const { allowsAdults, allowsChildren } = selectedEvent.ageRequirements;

    if (member.type === 'adult') {
      return allowsAdults;
    } else {
      return allowsChildren;
    }
  };

  const allMembers: FamilyMember[] = useMemo(() => {
    if (!family) return [];

    const members: FamilyMember[] = [
      {
        id: family.primaryAdult.id,
        type: AttendeeType.ADULT,
        firstName: family.primaryAdult.firstName,
        lastName: family.primaryAdult.lastName,
        email: family.primaryAdult.email,
        age: undefined,
        isPrimary: true,
      },
      ...family.adults
        .filter((adult) => adult.id !== family.primaryAdult.id)
        .map((adult) => ({
          id: adult.id,
          type: AttendeeType.ADULT,
          firstName: adult.firstName,
          lastName: adult.lastName,
          email: adult.email,
          age: undefined,
          isPrimary: false,
        })),
      ...family.children.map((child) => ({
        id: child.id,
        type: AttendeeType.CHILD,
        firstName: child.firstName,
        lastName: child.lastName,
        age: child.age,
        isPrimary: false,
      })),
    ];

    return members;
  }, [family]);

  const eligibleMembers: FamilyMember[] = useMemo(() => {
    if (!selectedEvent) return allMembers; // No event selected, show all
    return allMembers.filter(isMemberEligible);
  }, [allMembers, selectedEvent]);

  const ineligibleMembers: FamilyMember[] = useMemo(() => {
    if (!selectedEvent) return []; // No event selected, none are ineligible
    return allMembers.filter((member) => !isMemberEligible(member));
  }, [allMembers, selectedEvent]);

  return {
    family: family ?? null,
    loading,
    error,
    allMembers,
    eligibleMembers,
    ineligibleMembers,
    refetch,
  };
};
